import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { FiArrowLeft } from 'react-icons/fi';
import {
  AuthForm,
  AuthFormGroup,
  AuthLabel,
  AuthInput,
  AuthButton,
  AuthErrorMessage,
  AuthRow,
} from './authFormStyles';

const Arrow = FiArrowLeft as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const SignUpContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #ffffff;
  padding: 20px;
  position: relative;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;

  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 3.5rem;
  }
`;

const SignUpCard = styled.div`
  padding: 2rem;
  padding-top: 1rem;
  width: 100%;
  max-width: 480px;
  text-align: center;
  background: #ffffff;
  margin: 1.5rem auto;
  @media (max-width: 768px) {
    padding: 1.25rem;
    max-width: 100%;
  }
`;

const Title = styled.h1`
  color: var(--accent-orange);
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: var(--mobile-headline, 1.5rem);
    margin-bottom: 1.25rem;
  }
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 2px solid #e0e6ed;
  border-radius: var(--radius-md);
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  background: #ffffff;
  color: var(--text-gray);
  cursor: pointer;
  transition: var(--transition-normal);
  margin-bottom: 1rem;
  min-height: 2.75rem;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent-orange);
    background: rgba(255, 150, 0, 0.05);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    min-height: 3rem;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  text-align: center;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--card-border);
  }

  span {
    padding: 0 1rem;
    color: var(--text-gray);
    font-size: 0.85rem;
    font-family: 'Poppins', sans-serif;
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 2rem;
  left: 2rem;
  padding: 0.5rem;
  font-size: 1.2rem;
  border: 2px solid var(--accent-orange);
  border-radius: 50%;
  cursor: pointer;
  background: transparent;
  color: var(--accent-orange);
  transition: var(--transition-normal);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    background: var(--accent-orange);
    color: var(--text-light);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
  }
`;

const LinkText = styled.p`
  margin-top: 1.5rem;
  color: var(--text-gray);
  font-size: 0.85rem;
  font-family: 'Poppins', sans-serif;

  a {
    color: var(--accent-orange);
    text-decoration: none;
    font-weight: 600;
    transition: var(--transition-normal);

    &:hover {
      color: var(--accent-orange-dark);
      text-decoration: underline;
    }
  }
`;

const SignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const { loginWithGoogle, isLoading, error } = useAuth();
  const navigate = useNavigate();

  const validateStep1 = (): boolean => {
    if (!email.trim()) {
      setFormError('Please enter your email');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setFormError('Please enter a valid email address');
      return false;
    }
    if (!password) {
      setFormError('Please create a password');
      return false;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!validateStep1()) return;
    navigate('/signup/complete', {
      state: { email: email.trim().toLowerCase(), password },
      replace: false,
    });
  };

  const handleGoogleSignUp = () => {
    loginWithGoogle();
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <SignUpContainer>
      <BackButton onClick={handleBack} type="button" aria-label="Go back">
        <Arrow />
      </BackButton>
      <SignUpCard>
        <Title>Create Account</Title>

        {(error || formError) && (
          <AuthErrorMessage>
            {formError || error}
          </AuthErrorMessage>
        )}

        <GoogleButton onClick={handleGoogleSignUp} disabled={isLoading} type="button">
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </GoogleButton>

        <Divider>
          <span>OR</span>
        </Divider>

        <AuthForm onSubmit={handleSubmit}>
          <AuthFormGroup>
            <AuthLabel htmlFor="email">Email</AuthLabel>
            <AuthInput
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </AuthFormGroup>

          <AuthFormGroup>
            <AuthLabel htmlFor="password">Password</AuthLabel>
            <AuthInput
              id="password"
              name="password"
              type="password"
              placeholder="Create a password (min. 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </AuthFormGroup>

          <AuthFormGroup>
            <AuthLabel htmlFor="confirmPassword">Confirm Password</AuthLabel>
            <AuthInput
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </AuthFormGroup>

          <AuthButton type="submit" disabled={isLoading}>
            Continue
          </AuthButton>
        </AuthForm>

        <LinkText>
          Already have an account? <Link to="/signin">Sign in here</Link>
        </LinkText>
      </SignUpCard>
    </SignUpContainer>
  );
};

export default SignUp;
